import React from 'react';

import {
    setWidgetLoader,
    setGaugeWidgetConfiguration,
} from '@fiji/common/src/features/widgetManagement/widgetConfigurationSlice';

import { useAppDispatch, useTypedSelector } from '@fiji/common';
import { useGetAllLoadsQuery, useGetMultiDeviceLoadsQuery } from '@fiji/common/src/features/deviceManagement/deviceApi';

export const useHandleLoadsWidget = (): any => {
    const dispatch = useAppDispatch();
    const device = useTypedSelector((state) => state['widgetConfiguration']?.device);
    const multiDevice = useTypedSelector((state) => state['widgetConfiguration']['data']['multiDevice']);
    const isAutomated = useTypedSelector((state) => state['loads']['isAutomated']);
    const loadsPayload = useTypedSelector((state) => state['loads']);

    const { data, isLoading, isFetching, isError } = useGetAllLoadsQuery(
        { deviceId: device?.id, body: { page: 0, size: 25 } },
        { skip: !isAutomated || !device?.id || multiDevice, refetchOnMountOrArgChange: true }
    );
    const {
        data: allLoads,
        isError: multiLoadsError,
        isLoading: multiLoader,
        isFetching: multiFetcher,
    } = useGetMultiDeviceLoadsQuery(
        [
            {
                deviceId: device?.id,
                ...(!isAutomated && { loadId: loadsPayload?.secondary?.map((item: any) => item.loadId) }),
            },
        ],
        {
            skip: multiDevice || !loadsPayload?.secondary?.length || isAutomated || !device?.id,
            refetchOnMountOrArgChange: true,
        }
    );

    const isAnyLoaderActive = isFetching || isLoading || multiLoader || multiFetcher;
    const hasAnyLoadError = isError || multiLoadsError;

    React.useEffect(() => {
        if (isAnyLoaderActive) dispatch(setWidgetLoader(true));
    }, [isAnyLoaderActive]);

    React.useEffect(() => {
        if (hasAnyLoadError) dispatch(setWidgetLoader(false));
    }, [hasAnyLoadError]);

    React.useEffect(() => {
        if (!device?.id && !multiDevice) {
            dispatch(setWidgetLoader(false));
        }
    }, [device?.id, multiDevice, isAutomated]);

    React.useEffect(() => {
        if (data?.data?.loadConfiguration && !multiDevice) {
            const clone = JSON.parse(JSON.stringify(loadsPayload));
            delete clone['multiDevice'];
            if (clone?.isAutomated) {
                clone['secondary'] = data?.data?.loadConfiguration?.map((item: any) => ({
                    ...item,
                    deviceId: device?.id,
                    deviceName: device?.name,
                }));
                dispatch(setGaugeWidgetConfiguration(clone));
            }
        }
        // this handles when device-loads api response gives null data
        if (data?.data === null && !multiDevice) {
            dispatch(setGaugeWidgetConfiguration(loadsPayload));
        }
    }, [data, loadsPayload, multiDevice]);

    React.useEffect(() => {
        if (allLoads?.data?.response && !multiDevice) {
            const clone = JSON.parse(JSON.stringify(loadsPayload));
            delete clone['multiDevice'];
            if (clone?.secondary?.length && !clone?.isAutomated) {
                const resolvedLoads =
                    allLoads?.data?.response
                        ?.map((item: any) =>
                            item?.configurationList?.map((loadItem: any) => ({ ...loadItem, deviceId: item?.deviceId }))
                        )
                        ?.flat(Infinity) ?? [];
                const resolvedById = new Map(resolvedLoads?.map((item: any) => [item?.loadId, item]));
                clone['secondary'] = clone?.secondary?.map((selected: any) => {
                    const resolved = resolvedById.get(selected?.loadId);
                    return resolved ? { ...resolved, ...selected } : selected;
                });
                dispatch(setGaugeWidgetConfiguration(clone));
            }
        }
        // this handles when device-loads api response gives null data
        if (allLoads?.data === null && !multiDevice) {
            dispatch(setGaugeWidgetConfiguration(loadsPayload));
        }
    }, [allLoads, loadsPayload, multiDevice]);
};
